<template>
  <div class="content-builder">
    <div class="form-group">
      <label class="control-label" for="question_text">Question Text</label>
      <QuillEditor v-model:content="content.question.text" :contentType="'html'" :modules="editorModules"
                   :toolbar="toolbarOpts"
                   theme="snow"/>
    </div>
    <div class="form-group">
      <label class="control-label" for="question-text-classes">Question Text CSS Classes</label>
      <input id="question-text-classes" v-model="content.questionTextClasses" class="form-control" type="text">
    </div>
    <div class="form-group">
      <label class="control-label" for="user-input-classes">User Input CSS Classes</label>
      <input id="user-input-classes" v-model="content.userInputClasses" class="form-control" type="text">
    </div>
    <div class="form-group">
      <label class="control-label">Question Image</label>
      <div class="input-group">
        <div class="input-group-prepend">
          <a class="btn btn-primary text-white" data-input="question-image" data-lfm="image">
            <i class="fa fa-image"></i>
          </a>
        </div>
        <input id="question-image" v-model="content.questionImage" class="form-control" type="text">
      </div>
    </div>
    <div class="form-group">
      <label class="control-label" for="ideal_text">Ideal Text</label>
      <QuillEditor v-model:content="content.question.idealText" :contentType="'html'" :modules="editorModules"
                   :toolbar="toolbarOpts"
                   theme="snow"/>
    </div>
    <div class="form-group">
      <label class="control-label" for="ideal-text-classes">Ideal Text CSS Classes</label>
      <input id="ideal-text-classes" v-model="content.idealTextClasses" class="form-control" type="text">
    </div>
    <div class="form-group">
      <label class="control-label" for="point_selection_text">Compare Text Directions</label>
      <QuillEditor v-model:content="content.question.pointSelectionLabel" :contentType="'html'" :modules="editorModules"
                   :toolbar="toolbarOpts"
                   theme="snow"/>
    </div>
    <div class="form-group">
      <label class="control-label" for="compare-text-classes">Compare Text CSS Classes</label>
      <input id="compare-text-classes" v-model="content.compareTextClasses" class="form-control" type="text">
    </div>

    <AnimationControlsComponent :content="content"></AnimationControlsComponent>

    <div class="form-group">
      <div class="actions">
        <div class="action-item">
          <button class="btn btn-default" type="button"
                  @click="addResponse"><i class="fa fa-plus"></i> Add Scoring Option
          </button>
        </div>
        <div class="action-item">
          <input id="multiselect" v-model="content.question.multiselect" class="mr-1" type="checkbox">
          <label class="control-label" for="multiselect">Multiple Select (with Submit)</label>
        </div>
        <div class="action-item">
          <input id="randomize" v-model="content.question.randomize" class="mr-1" type="checkbox">
          <label class="control-label" for="randomize">Randomize Responses</label>
        </div>
        <div class="action-item">
          <input id="show-0-val-outcomes" v-model="content.showOutcomeOfZero" class="mr-1" type="checkbox">
          <label class="control-label" for="show-0-val-outcomes">Show outcomes with value of 0</label>
        </div>
      </div>
      <div class="content-list">
        <div v-for="(response, index) in content.question.responses" v-cloak :key="index"
             class="card content-list-item">
          <div class="card-header text-center font-weight-bold">{{ alphabet[index] }}</div>
          <div class="card-body">
            <div class="form-group">
              <label class="control-label" for="label">Choice Text</label>
              <QuillEditor v-model:content="response.label" :contentType="'html'" :options="editorOptsLite"
                             theme="snow"/>
            </div>
            <div class="form-group row">
              <div class="col-sm-3">
                <label class="control-label" for="flow">Flow Hook</label>
                <input v-model="response.flow" :placeholder="'choice-' + (index + 1)" class="form-control"
                       type="text">
              </div>
              <div class="col-sm-3">
                <label class="control-label" for="flow">Outcome Trigger</label>
                <input v-model="response.trigger" :placeholder="'event-' + (index + 1)" class="form-control"
                       type="text">
              </div>
            </div>
            <div class="form-group card-metrics">
              <label class="control-label" for="metrics">Meter Effects</label>
              <div class="row">
                <div v-for="meter in response.meters" :key="meter.id" class="col-sm">
                  <span :class="['badge', 'badge-primary', meter.id]">{{ meter.id }}</span>
                  <input :id="meter.id" v-model="meter.value" class="form-control" type="number">
                </div>
                <div class="col-sm offset-sm-3">
                  <label class="control-label">&nbsp;</label>
                  <button class="btn btn-danger btn-block" type="button"
                          @click="remove(response, index)">Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-show="content.question.responses.length > 0">
        <div class="action-item">
          <button class="btn btn-default" type="button"
                  @click="addResponse"><i class="fa fa-plus"></i> Add Scoring Option
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import {defaultMeters} from '../../../common/metrics'
import layoutMixin from '../../mixins/layout'
import limitedMixin from '../../mixins/limited'
import AnimationControlsComponent from "../AnimationControlsComponent.vue"
import {EDITOR_CONFIG_BUTTONS_FULL, EDITOR_CONFIG_LITE} from '../../../common/constants'
import {QuillEditor} from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
  components: {
    QuillEditor,
    AnimationControlsComponent
  },
  mixins: [layoutMixin, limitedMixin],
  data() {
    const content = _.cloneDeep(this.source)
    const filled = _.defaults(content, {
      question: {
        text: '',
        idealText: '',
        feedback: '',
        pointSelectionLabel: '',
        userEnteredText: '',
        multiselect: false,
        timeLimit: false,
        responses: []
      },
      animationEffects: {
        responseDisplay: '',
        responseStartDelay: 0,
        responseBetweenDelay: 0.2
      },
      questionTextClasses: '',
      userInputClasses: '',
      idealTextClasses: '',
      compareTextClasses: ''
    })

    // pre-fill with default data
    filled.question.responses.forEach((response) => {
      if (!response.meters || !response.meters.length) {
        response.meters = defaultMeters()
      }
    })

    if (!_.get(content, 'showOutcomeOfZero')) {
      content.showOutcomeOfZero = false;
    }

    if (!_.get(filled.question, 'questionTextClasses')) {
      filled.question.questionTextClasses = ''
    }

    if (!_.get(filled.question, 'userInputClasses')) {
      filled.question.userInputClasses = ''
    }

    if (!_.get(filled.question, 'idealTextClasses')) {
      filled.question.idealTextClasses = ''
    }

    if (!_.get(filled.question, 'compareTextClasses')) {
      filled.question.compareTextClasses = ''
    }

    if (!_.get(filled.question, 'animationEffects')) {
      filled.question.animationEffects = {
        responseDisplay: '',
        responseStartDelay: 0,
        responseBetweenDelay: 0.2
      }
    }

    console.log(filled)

    return {
      content: filled,
      limit: 20,
      toolbarOpts: EDITOR_CONFIG_BUTTONS_FULL,
      editorOptsLite: EDITOR_CONFIG_LITE,
    }
  },
  computed: {
    limitedItem() {
      return this.content.question.responses
    }
  },
  methods: {
    setContent() {
    },
    getValue() {
      return this.content
    },
    remove(item, index) {
      this.content.question.responses.splice(index, 1)
    },
    addResponse() {
      this.content.question.responses.push({
        meters: defaultMeters()
      })
      this.$nextTick(() => {
        $(this.$el).find('[data-lfm]').filemanager('image', {prefix: '/filemanager'})
      })
    }
  }
}
</script>

<style lang="scss">
.content-list {
  clear: both;

  .form-group {

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
